import { Box, Divider, Typography } from "@mui/material";
import { useCallback, useMemo, useReducer } from "react";
import EnhancedTable from "../../../enhancedTable/EnhancedTable";
import { isLocalDev } from "../../../../utils/environment";
import { ValidDataService } from "../../../../service/validDataService";
import useAbortController from "../../../../hooks/useAbortController";
import useDialogModal from "../../../../hooks/useDialogModal";
import validOrderServiceInputForm from "./validServiceInputForm";
import ConfirmationDialog from "../../../ui/confirmationDialog";
import { toast } from "react-toastify";
import validLookup from "../../../../utils/validLookup";
import editValidOrderServiceForm from "./editValidServiceForm";
import { validServiceColumns } from './columns'
import EditIcon from "@mui/icons-material/Edit";

export default function ValidService(props) {
    const [ValidOrderServicesInputDialog, showValidOrderServicesInputForm] = useDialogModal(validOrderServiceInputForm);
    const [ValidOrderServicesEditDialog, showValidOrderServicesEditForm] = useDialogModal(editValidOrderServiceForm);

    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);

    const [signal, abortRequest] = useAbortController();
    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params };
    }, {
        data: [],
        totalRowCount: 0,
        pageCount: 0,
        filter: '',
        loading: false,
    });

    const onAddRecordClicked = () => {
        showValidOrderServicesInputForm();
    }

    const handleRowEdit = (rowData) => {
        showValidOrderServicesEditForm(null, { validOrder: rowData });
    }

    /**
    * fetch data
    */
    const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
        const from = pageSize * pageIndex;
        updateState({ loading: true });
        ValidDataService.getOrderServicesWithLimit(from, pageSize, filters, sortBy, signal).then(result => {
            const { code, data } = result;
            if (code === 0) {
                updateState({
                    data: data.data,
                    totalRowCount: data.count,
                    pageCount: Math.ceil(data.count / pageSize),
                });
            }
            updateState({ loading: false });
        }).catch(error => {
            if (isLocalDev) {
                console.log(error)
            }
            updateState({ loading: false });
        });
    }, []);

    const deleteRecordHandler = (rowids) => {
        showConfirmationDialog(async (action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                const deleteRecordResult = await ValidDataService.deleteOrderServicesRecord(rowids.map(x => parseInt(x, 10)));
                const { code, message, data } = deleteRecordResult;

                if (code === 0) {
                    toast.success(message);
                    const updateList = state.data.filter(d => !rowids.map(x => parseInt(x, 10)).includes(d.id));
                    updateState({ data: updateList });
                } else {
                    toast.success("Failed to delete record(s)");
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Delete record',
            message: `Please confirm if you would like to delete ${rowids.length} record(s)?`,
        });
    }

    const recordAddHandler = (newRecord) => {
        const newData = state.data.concat(newRecord);
        updateState({ data: newData });
    }

    const recordEditHandler = (record) => {
        // find the employee by row id
        const existingValidOrderServides = state.data;
        const idx = existingValidOrderServides.findIndex(e => e.id === record.id);
        const updatesList = existingValidOrderServides.map((row, index) => {
            if (row.id === record.id) {
                return {
                    ...existingValidOrderServides[idx], ...record,
                }
            }
            return row
        });

        // update list
        updateState({
            data: updatesList
        });
    }

    return (
        <Box>
            <Typography variant="h4">Services</Typography>
            <Divider sx={{mb: 2}} />
            <EnhancedTable
                columns={validServiceColumns}
                addRecordHandler={onAddRecordClicked}
                deleteRecordHandler={deleteRecordHandler}
                data={state.data}
                totalRowCount={state.totalRowCount}
                pageCount={state.pageCount}
                fetchData={fetchData}
                loading={state.loading}
                selectionMode="multi"
                actions={[
                    {
                        icon: <EditIcon />,
                        onClick: handleRowEdit
                    },
                ]}
            />
            <ValidOrderServicesInputDialog onRecordAdded={recordAddHandler} />
            <ValidOrderServicesEditDialog onRecordEdit={recordEditHandler} />

            <ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel",
                }} />
        </Box>
    )
}